// Chrome Push Notifications Service
export class NotificationService {
  private static instance: NotificationService;
  
  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  // Check if notifications are supported
  public isSupported(): boolean {
    return 'Notification' in window;
  }

  // Get current permission status
  public getPermission(): NotificationPermission {
    if (!this.isSupported()) return 'denied';
    return Notification.permission;
  }

  // Request notification permission
  public async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      console.warn('Notifications are not supported in this browser');
      return 'denied';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      return 'denied';
    }

    // Request permission
    const permission = await Notification.requestPermission();
    return permission;
  }

  // Show notification
  public showNotification(title: string, options?: NotificationOptions): Notification | null {
    if (!this.isSupported() || Notification.permission !== 'granted') {
      console.warn('Cannot show notification: permission not granted or not supported');
      return null;
    }

    const defaultOptions: NotificationOptions = {
      icon: '/favicon.ico', // Use favicon or you can add a proper notification icon
      badge: '/favicon.ico',
      requireInteraction: false,
      silent: false,
      ...options
    };

    try {
      const notification = new Notification(title, defaultOptions);
      
      // Auto close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }

  // Show order notification specifically
  public showOrderNotification(restaurant: string, createdBy: string, deadline: number): Notification | null {
    // Format deadline from minutes to readable time
    const formatDeadline = (minutes: number): string => {
      if (minutes >= 60) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
      }
      return `${minutes}m`;
    };

    const deadlineText = formatDeadline(deadline);
    
    return this.showNotification(
      '🍜 Order mới đã được tạo!',
      {
        body: `${createdBy} vừa tạo order tại ${restaurant}\nThời gian: ${deadlineText} để chọn món`,
        icon: '🍜',
        tag: 'new-order', // Prevent duplicate notifications
        requireInteraction: true
      }
    );
  }

  // Check if notifications are enabled in user settings
  public isEnabledInSettings(): boolean {
    const settings = localStorage.getItem('userNotificationSettings');
    if (!settings) return true; // Default to enabled
    
    try {
      const parsed = JSON.parse(settings);
      return parsed.notifications !== false;
    } catch {
      return true;
    }
  }

  // Save notification setting
  public setEnabledInSettings(enabled: boolean): void {
    const current = localStorage.getItem('userNotificationSettings');
    let settings: any = {};
    
    if (current) {
      try {
        settings = JSON.parse(current);
      } catch {
        settings = {};
      }
    }
    
    settings.notifications = enabled;
    localStorage.setItem('userNotificationSettings', JSON.stringify(settings));
  }
}

export const notificationService = NotificationService.getInstance();
